// frontend/shared/src/money.ts —— D8-12 / TD-814：BigInt 微单位（1e-6），展示截断
export type Micro = bigint;
const SCALE = 1_000_000n;
const DEC_RE = /^(-)?(\d+)(?:\.(\d{1,6}))?$/;

export function toMicro(s: string): Micro {
  const m = DEC_RE.exec(s.trim());
  if (!m || m[2] === undefined) throw new Error(`非法金额：${s}`);
  const int = BigInt(m[2]);
  const frac = BigInt((m[3] ?? '').padEnd(6, '0'));
  const v = int * SCALE + frac;
  return m[1] ? -v : v;
}

export function fromMicro(v: Micro): string {
  const neg = v < 0n;
  const a = neg ? -v : v;
  return `${neg ? '-' : ''}${a / SCALE}.${String(a % SCALE).padStart(6, '0')}`;
}

export const add = (a: Micro, b: Micro): Micro => a + b;
export const sub = (a: Micro, b: Micro): Micro => a - b;
export const cmp = (a: Micro, b: Micro): -1 | 0 | 1 => (a < b ? -1 : a > b ? 1 : 0);
export const isZero = (a: Micro): boolean => a === 0n;
export const max = (a: Micro, b: Micro): Micro => (a > b ? a : b);

function divHalfUp(n: bigint, d: bigint): bigint {
  if (n < 0n) return -divHalfUp(-n, d);
  return (n * 2n + d) / (2n * d);
}

// amount × ratePct%（ratePct 为十进制百分数串，如 "2.5"；02 F-06：6 位 HALF_UP，负数按绝对值进位）
export function mulRateHalfUp(amount: Micro, ratePct: string): Micro {
  return divHalfUp(amount * toMicro(ratePct), 100n * SCALE);
}

function group(intPart: string): string {
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

// D8-12：展示按 dp 位向零截断（不四舍五入）、补零；截断后为 0 不带负号
export function fmtAmount(v: string | Micro, dp: number, opts: { group?: boolean } = {}): string {
  if (!Number.isInteger(dp) || dp < 0 || dp > 6) throw new Error(`dp 越界：${dp}`);
  const micro = typeof v === 'bigint' ? v : toMicro(v);
  const unit = 10n ** BigInt(6 - dp);
  const truncated = micro / unit; // BigInt 除法向零截断
  const neg = truncated < 0n;
  const a = neg ? -truncated : truncated;
  const base = 10n ** BigInt(dp);
  const intStr = String(a / base);
  const fracStr = dp > 0 ? '.' + String(a % base).padStart(dp, '0') : '';
  return `${neg ? '-' : ''}${opts.group === false ? intStr : group(intStr)}${fracStr}`;
}

// 表单输入规范化：仅非负十进制，小数位 ≤ dp；非法 / 超位数返回 null（调用方显示字段错误）
export function parseDecimalInput(raw: string, dp: number): string | null {
  const s = raw.trim();
  const m = /^(\d*)(?:\.(\d*))?$/.exec(s);
  if (!m || s === '' || s === '.') return null;
  const int = m[1] === '' || m[1] === undefined ? '0' : m[1];
  const frac = m[2] ?? '';
  if (frac.length > dp) return null;
  return frac ? `${int}.${frac}` : int;
}

// 卡交易外币消费的原币金额副标题（2026-10-19 线上外币消费修复）：后端 billing_amount 已是卡币种金额，
// original_amount / original_currency 仅外币消费时下发。展示「176,000 VND」（绝对值、去尾零全精度、千分位）；
// 任一字段缺失或金额畸形时返回空串（只是副标题，不阻断列表）。
export function fmtOriginal(amount: string | null | undefined, currency: string | null | undefined): string {
  const cur = (currency ?? '').trim().toUpperCase();
  if (!amount || !cur) return '';
  try {
    return `${fmtPlain(amount.startsWith('-') ? amount.slice(1) : amount)} ${cur}`;
  } catch {
    return '';
  }
}

// 去尾零展示（佣金类，对客评审 2026-10-15 #26）：6 位全精度、不舍入，去掉尾随 0 与多余小数点；默认千分位
// "12.500000" → "12.5"，"1234.000000" → "1,234"，"0.000000" → "0"
export function fmtPlain(v: string | Micro, opts: { group?: boolean } = {}): string {
  const s = fmtAmount(v, 6, opts);
  return s.replace(/0+$/, '').replace(/\.$/, '');
}

// 佣金 rate_text 快照展示（backend commission.rateText）：新快照开卡「0.6 USDT」/ 差额链「1−0.6 USDT」、充值「10%」原样；
// 2026-10-15 前的旧快照开卡「$0.60」/「$1−$0.6」转为「0.6 USDT」/「1−0.6 USDT」（对客评审 #26：佣金单位统一 USDT）
export function commissionRateText(raw: string): string {
  if (!raw.includes('$')) return raw;
  const trimmed = raw.replace(/\$(\d+(?:\.\d+)?)/g, (_m, n: string) => (n.includes('.') ? n.replace(/0+$/, '').replace(/\.$/, '') : n));
  return trimmed.endsWith(' USDT') ? trimmed : `${trimmed} USDT`;
}

// 佣金金额「{金额} USDT」（钱包余额与佣金为 USDT、卡相关为 $，用户 2026-10-15 拍板）；非法 / 空值按 fallback 原样返回
export function fmtCommission(v: string | Micro | null | undefined, opts: { group?: boolean; fallback?: string } = {}): string {
  if (v === null || v === undefined || v === '') return opts.fallback ?? '0 USDT';
  try {
    return `${fmtPlain(v, opts)} USDT`;
  } catch (e) {
    console.warn('[gp:money] 佣金金额格式非法', v, e);
    return opts.fallback ?? String(v);
  }
}
